import {
  getUserRoleCodeFromStorage,
  isValidRole,
  isAutoModeViewerRoleCode,
} from "../utils/utils";

/** viewTypePermission: true when not hotelApp (matches SideNavigation). */
export const getViewTypePermission = () =>
  localStorage.getItem("viewType") !== "hotelApp";

// Normalize "/hms/foo" or "foo" to the route map key used in routes/index.js.
export const normalizeRoutePath = (path = "") => {
  let key = String(path).trim();
  if (key.startsWith("/hms/")) key = key.slice(5);
  else if (key.startsWith("/hms")) key = key.slice(4);
  else if (key.startsWith("/")) key = key.slice(1);
  return key;
};

/**
 * Evaluate a declarative access rule (same semantics as SideNavigation).
 *
 * Role model:
 * - ROLE_SUPER_ADMIN: all routes except explicit exclusions (excludeSuperAdmin / Master APK)
 * - ROLE_ADMIN and all other roles : permission-based only
 * - Route-specific flags preserve prior SideNav behavior (permissionOrAutoModeViewer on
 *   Auto Mode Report, viewType gates, Super Admin–only items, allowAdminBypassViewType
 *   on Screensaver for ROLE_ADMIN on third-party hotels, etc.)
 */
export const evaluateAccess = (rule = {}) => {
  if (!rule || typeof rule !== "object") return false;

  const userRoleCode = getUserRoleCodeFromStorage();
  const isSuperAdmin = userRoleCode === "ROLE_SUPER_ADMIN";
  const isAdmin = userRoleCode === "ROLE_ADMIN";
  const isAutoModeViewer = isAutoModeViewerRoleCode(userRoleCode);
  const viewTypePermission = getViewTypePermission();

  if (rule.authenticatedOnly) {
    return Boolean(localStorage.getItem("jwt"));
  }

  // Super Admin: full access except routes historically hidden from them
  if (isSuperAdmin) {
    if (rule.excludeSuperAdmin || rule.requireNonSuperAdmin) return false;
    if (rule.requireNonHotelAppView) return false;
    return true;
  }

  // --- Admin and all other roles: permission-based ---

  if (rule.superAdminOnly) {
    return false;
  }

  if (rule.permissionOrAutoModeViewer) {
    // Auto Mode Report visible if AUTO_MODE_REPORT OR Auto Mode Viewer role
    if (isAutoModeViewer) return true;
    return rule.permission ? isValidRole(rule.permission) : false;
  }

  if (rule.superAdminOrPermission) {
    return rule.permission ? isValidRole(rule.permission) : false;
  }

  if (rule.requireNonHotelAppView) {
    // Master APK: non–super-admin + hotelApp view + permission
    if (viewTypePermission) return false;
    return rule.permission ? isValidRole(rule.permission) : false;
  }

  if (rule.permission && !isValidRole(rule.permission)) {
    return false;
  }

  if (rule.requireViewType && !viewTypePermission) {
    if (!(rule.allowAdminBypassViewType && isAdmin)) {
      return false;
    }
  }

  if (rule.permission) return true;

  return false;
};

/** viewType-gated menu items (Super Admin bypasses viewType via evaluateAccess). */
const withViewType = (permission) => ({
  permission,
  requireViewType: true,
});

/**
 * Complete map: every /hms child path from routes/index.js must appear here.
 * Aliases (e.g. cityConfiguration) keep SideNavigation links in sync.
 */
export const ROUTE_PERMISSIONS = {
  // Dashboards
  "Dashboard/Hotels_Dashboard": { permission: "MENU_HOTEL_DASHBOARD" },
  "Dashboard/Test_Demo_Hotels_Dashboard": { permission: "MENU_HOTEL_DASHBOARD" },
  "Dashboard/AssetManagement_Dashboard": { permission: "MENU_HOTEL_DASHBOARD" },
  "Dashboard/APK_Version_Dashboard": { permission: "MENU_HOTEL_DASHBOARD" },
  "Dashboard/Automode_Dashboard": { permission: "MENU_HOTEL_DASHBOARD" },

  // Hotel / room / wizard config
  ConfigurationNewHotel: { permission: "MENU_CONFIGURATION_NEW_HOTEL" },
  HotelOnboardingWizard: {
    permission: "MENU_CONFIGURATION_NEW_HOTEL",
    excludeSuperAdmin: true,
  },
  ConfigurationMasterApk: {
    permission: "MENU_MASTER_APK_CONFIGURATION",
    requireNonHotelAppView: true,
  },
  ConfigurationNewRoom: withViewType("MENU_CONFIGURATION_NEW_ROOM"),
  WelcomeScreenConfig: withViewType("MENU_CUSTOMIZE_WELCOME_SCREEN"),
  WelcomeMessages: withViewType("MENU_WELCOME_MESSAGES"),
  HeroBanner: withViewType("MENU_CONFIGURATION_MANAGEMENT"),
  RailConfiguration: withViewType("MENU_CONFIGURATION_RAIL"),
  HotelServiceConfiguration: withViewType("MENU_CONFIGURATION_HOTEL_SERVICE"),
  HotelChannels: withViewType("MENU_CONFIGURATION_HOTEL_CHANNEL"),
  ScreensaverConfiguration: {
    permission: "MENU_CONFIGURATION_MASTER_SCREENSAVER",
    requireViewType: true,
    allowAdminBypassViewType: true,
  },
  ConfigurationBackgroundImage: withViewType(
    "MENU_CONFIGURATION_MASTER_BACKGROUND_IMAGE"
  ),
  CityConfiguration: withViewType("MENU_CITY_CONFIGURATION"),
  cityConfiguration: withViewType("MENU_CITY_CONFIGURATION"),
  GroupRooms: withViewType("MENU_GROUP_ROOMS"),

  // CDSN / Auto Mode / ops
  fetchcdsndetails: {
    permission: "CDSN_DETAILS_CONFIGURATION",
    superAdminOnly: true,
  },
  fetchcdsndetailsAdmin: {
    permission: "SID_CDSN_DETAILS",
  },
  autoModeReport: {
    permission: "AUTO_MODE_REPORT",
    permissionOrAutoModeViewer: true,
  },
  resyncchainid: { permission: "RE_SYNC_CHAINID" },
  activityLogs: { superAdminOnly: true },
  guestMessage: withViewType("MENU_GUEST_MESSAGE"),

  // Master config
  DefaultConfig: withViewType("MENU_DEFAULT_CONFIGURATION"),
  WhitelistDomain: { superAdminOnly: true },
  PartnerConfiguration: { permission: "MENU_CONFIGURATION_MASTER_PARTNER" },
  RoomTypeConfiguration: {
    permission: "MENU_CONFIGURATION_MASTER_ROOM_TYPE",
  },
  APKProviderConfiguration: {
    permission: "MENU_CONFIGURATION_MASTER_APK_PROVIDER",
  },
  thirdPartyHotelConfiguration: {
    permission: "MENU_CONFIGURATION_MASTER_THIRD_PARTY_HOTEL",
  },
  UtilityServiceConfiguration: {
    permission: "MENU_CONFIGURATION_MASTER_UTILITIES",
  },
  SatelliteChannelMapping: {
    permission: "MENU_CONFIGURATION_MASTER_SATELLITE_CHANNEL_MAPPING",
    superAdminOrPermission: true,
  },
  SoftwareStackDetails: {
    permission: "SOFTWARE_STACK_DETAILS",
    superAdminOrPermission: true,
  },

  // User management
  userList: { permission: "MENU_USERS" },
  createEditUser: { permission: "MENU_USER_CREATE" },
  editUser: { permission: "MENU_USERS" },
  "pending-approvals": { permission: "MENU_USERS" },
  roles: { permission: "MENU_ROLES" },
  "create-role": { permission: "MENU_ROLES" },
  "edit-role": { permission: "MENU_ROLES" },
  userPrivilege: { permission: "MENU_USER_PRIVILEGES" },
  rolesPermissions: { permission: "MENU_ROLES_AND_PERMISSIONS" },

  // Authenticated-only (any logged-in user)
  myAccount: { authenticatedOnly: true },
  changePassword: { authenticatedOnly: true },
  helpManual: { authenticatedOnly: true },
};

/** Parent menu groups used by SideNavigation (not direct routes). */
export const MENU_GROUP_RULES = {
  dashboard: { permission: "MENU_HOTEL_DASHBOARD" },
  masterConfigurations: withViewType("MENU_CONFIGURATION_MASTER"),
  userManagement: withViewType("MENU_USER_MANAGEMENT"),
};

export const canAccessMenuGroup = (groupKey) => {
  const rule = MENU_GROUP_RULES[groupKey];
  if (!rule) return false;
  return evaluateAccess(rule);
};

export const canAccessRoute = (pathOrKey) => {
  const key = normalizeRoutePath(pathOrKey);
  const rule = ROUTE_PERMISSIONS[key];
  if (!rule) {
    // Unknown route under /hms: deny (fail closed)
    return false;
  }
  return evaluateAccess(rule);
};
